// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import { type AskPunttAgentStep } from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useState } from "react";

// Keep in sync with AgentSteps status panel tokens (avoid circular import).
const STATUS_PANEL_TOGGLE_MUTED_CLASS =
  "flex w-full items-center gap-2 px-2.5 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7 bg-transparent";
const STATUS_PANEL_ITEMS_MUTED_CLASS = "bg-transparent px-1.5 py-1.5";

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type SubagentRequest = { name: string; task: string };

type SubagentReportView = {
  name?: string;
  status?: string;
  summary?: string;
  error?: string;
  output?: string;
};

function stringField(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseSubagentRequests(
  args: Record<string, unknown> | undefined,
): SubagentRequest[] {
  const requests = args?.requests;
  if (!Array.isArray(requests)) return [];
  return requests
    .map((entry) => {
      if (entry == null || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const name = stringField(row.name);
      const task = stringField(row.task);
      if (!name) return null;
      return { name, task };
    })
    .filter((row): row is SubagentRequest => row != null);
}

function parseSubagentReports(summary: string): SubagentReportView[] | null {
  const trimmed = summary.trim();
  if (!trimmed.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.map((entry) =>
      entry != null && typeof entry === "object"
        ? (entry as SubagentReportView)
        : {},
    );
  } catch {
    return null;
  }
}

function parseSubagentReport(summary: string): SubagentReportView | null {
  const trimmed = summary.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return parsed != null && typeof parsed === "object"
      ? (parsed as SubagentReportView)
      : null;
  } catch {
    return null;
  }
}

function reportDetail(report: SubagentReportView | undefined): string {
  if (!report) return "";
  const summary = stringField(report.summary);
  if (summary) return summary;
  const error = stringField(report.error);
  if (error) return error;
  const output = stringField(report.output);
  if (output) return output;
  const status = stringField(report.status);
  return status;
}

function SubagentToolStep({
  stepId,
  requestIndex,
  title,
  body,
  report,
  done,
}: {
  stepId: string;
  requestIndex?: number;
  title: string;
  body: string;
  report?: SubagentReportView;
  done: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const suffix = requestIndex != null ? `-${requestIndex}` : "";
  const rowId = `${stepId}${suffix}`;
  const reportText = reportDetail(report);

  return (
    <div data-testid={`agent-step-subagent-${rowId}`}>
      <button
        type="button"
        data-testid={`agent-step-subagent-toggle-${rowId}`}
        aria-expanded={expanded}
        aria-label={title}
        onClick={() => setExpanded((open) => !open)}
        className={STATUS_PANEL_TOGGLE_MUTED_CLASS}
      >
        {done ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-11"
        >
          {title}
        </Text>
        {expanded ? (
          <CaretDown
            size={10}
            className="shrink-0 text-puntt-neutral-gray-11"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={10}
            className="shrink-0 text-puntt-neutral-gray-11"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-body-${rowId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          {body ? (
            <Text
              size="1"
              className="whitespace-pre-wrap text-puntt-neutral-gray-11"
            >
              {body}
            </Text>
          ) : null}
          {reportText ? (
            <Text
              size="1"
              className={`whitespace-pre-wrap text-puntt-neutral-gray-9 ${body ? "mt-1" : ""}`}
            >
              {reportText}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** One expandable row per subagent spawn / skill load. */
export function SubagentToolStepsForPair({
  pair,
  isStreaming,
}: {
  pair: StepPair;
  isStreaming: boolean;
}) {
  const call = pair.call;
  const done = !!pair.result || !isStreaming;
  const stepId = call.stepId || call.timestamp;
  const args = call.args ?? {};

  if (call.tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    const reports = pair.result
      ? parseSubagentReports(pair.result.summary)
      : null;
    if (requests.length === 0) {
      return (
        <SubagentToolStep
          stepId={stepId}
          title={call.displayName || "Run subagents"}
          body=""
          done={done}
        />
      );
    }
    return (
      <>
        {requests.map((request, index) => (
          <SubagentToolStep
            key={`${stepId}-${index}`}
            stepId={stepId}
            requestIndex={index}
            title={request.name}
            body={request.task}
            report={reports?.[index]}
            done={done}
          />
        ))}
      </>
    );
  }

  if (call.tool === "run_subagent") {
    const name = stringField(args.name) || call.displayName || "Run subagent";
    const task = stringField(args.task);
    const report = pair.result
      ? (parseSubagentReport(pair.result.summary) ?? undefined)
      : undefined;
    return (
      <SubagentToolStep
        stepId={stepId}
        title={name}
        body={task}
        report={report}
        done={done}
      />
    );
  }

  if (call.tool === "load_capability") {
    const instructions = stringField(args.instructions);
    return (
      <SubagentToolStep
        stepId={stepId}
        title={call.displayName || "Load skill"}
        body={instructions}
        done={done}
      />
    );
  }

  return null;
}

export function countSubagentLogicalSteps(
  tool: string,
  args: Record<string, unknown> | undefined,
): number {
  if (tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    return requests.length > 0 ? requests.length : 1;
  }
  return 1;
}
